import React, { FC } from 'react';
import capitalize from 'lodash/capitalize';
import { SxProps } from '@mui/system';
import { mergeSx } from '../../utils/sx/merge';
import { useTheme } from '../../hooks/useTheme';
import { Box } from '../box';
import { Typography } from '../typography';
import { StyledUnorderedList } from '../styled/styledUnorderedList';
import { StyledListItem } from '../styled/styledListItem';
import { Tag } from '../tag';
import { Link } from '../link';
import { TAG_LIST_COLLAPSED_COUNT } from '../tag/const';
import { MoreProps, TagCloudProps } from './types';
export type { TagCloudProps } from './types';

const MAX_VISIBLE_ITEMS_BY_DEFAULT = TAG_LIST_COLLAPSED_COUNT;

export const TagCloud: React.FC<TagCloudProps> = ({
  title,
  items,
  maxVisibleItemsCountByDefault = MAX_VISIBLE_ITEMS_BY_DEFAULT,
  sx,
  size = 'normal',
  unfilled,
  slots,
}) => {
  const theme = useTheme();
  const [isExpanded, setExpanded] = React.useState(false);

  const style: Record<'mx' | 'my' | 'negative', Record<typeof size, number>> = {
    mx: {
      normal: 0.75,
      small: 0.25,
    },
    my: {
      normal: 1,
      small: 0.25,
    },
    negative: {
      normal: -0.75,
      small: -0.25,
    },
  };

  const filledSx: SxProps = !unfilled
    ? {
        p: 3.75,
        borderRadius: `${theme.borderRadius.extraLarge}px`,
        backgroundColor: 'white',
      }
    : {};
  const handleOpen = () => setExpanded(true);
  const More: FC<MoreProps> = slots?.more
    ? slots.more
    : ({ onOpen }) => (
        <Link onClick={onOpen} sx={{ mt: 0.75, mx: 0.75, display: 'block' }}>
          Показать ещё
        </Link>
      );

  return (
    <Box sx={mergeSx(filledSx, sx)}>
      {Boolean(title) && (
        <Typography component="h3" sx={{ mt: 0, mb: 1.5 }} variant="h6 Bold">
          {title}
        </Typography>
      )}

      <StyledUnorderedList sx={{ mx: style.negative[size], display: 'flex', flexWrap: 'wrap' }}>
        {items.map(({ key, label, ...restItemProps }, index) => (
          <StyledListItem
            key={key}
            sx={{
              my: style.my[size],
              mx: style.mx[size],
              display: index >= maxVisibleItemsCountByDefault && !isExpanded ? 'none' : undefined,
            }}
          >
            <Tag
              href={'href' in restItemProps ? restItemProps.href : undefined}
              onClick={'onClick' in restItemProps ? restItemProps.onClick : undefined}
            >
              {typeof label === 'string' ? capitalize(label) : label}
            </Tag>
          </StyledListItem>
        ))}
      </StyledUnorderedList>

      {!isExpanded && items.length > maxVisibleItemsCountByDefault && <More onOpen={handleOpen} />}
    </Box>
  );
};

export type { TagCloudItem } from './types';
